import { NationalityConstants } from "./constants";

/**
 * Groups individual country codes into regional clusters when all members are selected.
 * 
 * @param selectedNationalities - Array of country codes (e.g., ['AE', 'SA', ...])
 * @param masterData - Object containing clusters (e.g., ALL_NATIONS_OPTIONS and ALL_NATIONS_MAPS)
 * @returns Array of strings (codes) including cluster codes where applicable
 */
export const generateNationalityCluster = (
  selectedNationalities: string[],
  masterData: any
): string[] => {
  if (!selectedNationalities || selectedNationalities.length === 0) return [];

  const clusters = masterData?.allNationsOptions || masterData?.ALL_NATIONS_OPTIONS || [];
  const maps = masterData?.allNationsMaps || masterData?.ALL_NATIONS_MAPS || {};

  if (clusters.length === 0) return selectedNationalities;

  const result: string[] = [];
  const selectedSet = new Set(selectedNationalities);
  const subsumedCodes = new Set<string>();

  clusters.forEach((cluster: any) => {
    const clusterCode = cluster.code;
    const children = maps[clusterCode] || [];

    if (children.length > 0) {
      const isCompletelySelected = children.every((childCode: string) =>
        selectedSet.has(childCode)
      );

      if (isCompletelySelected) {
        result.push(clusterCode);
        children.forEach((childCode: string) => subsumedCodes.add(childCode));
      }
    }
  });

  selectedNationalities.forEach((code) => {
    if (!subsumedCodes.has(code)) {
      result.push(code);
    }
  });
  return Array.from(new Set(result));
};

/**
 * Removes a nationality or a whole cluster of nationalities.
 * 
 * @param selectedNationalities - Current array of country codes
 * @param removedItem - The code to remove (can be a cluster code or a single country code)
 * @param masterData - Object containing clusters
 * @returns Updated array of country codes
 */
export const removeNationalityCluster = (
  selectedNationalities: string[],
  removedItem: string,
  masterData: any
): string[] => {
  if (!selectedNationalities || selectedNationalities.length === 0) return [];
  if (!removedItem) return selectedNationalities;
  if (removedItem === "XX") {
    return [];
  }

  const maps = masterData?.allNationsMaps || masterData?.ALL_NATIONS_MAPS || {};
  const children = maps[removedItem];

  if (children && Array.isArray(children)) {
    const childrenSet = new Set(children);
    return selectedNationalities.filter((code) => !childrenSet.has(code) && code !== removedItem);
  }

  return selectedNationalities.filter((code) => code !== removedItem);
};
